import scrollMonitor from 'scrollmonitor';
import {publishEvent} from '../helpers/utilities';
import {callbackOnElements, whichTransition} from '@borngroup/born-utilities';

/**
 * Adds special handling to elements to display different types of motion.
 * Timing is mostly handled on the JS, while actual transitions are carried over on CSS.
 */
export default class PageMotion {
    constructor(options) {
        this.motionInterval = 100;
        this.elementOffset = -100;

        callbackOnElements(options.elements, this._setupMotionEls.bind(this));
    }

    _setupMotionEls(currentPageEl) {
        this._getElementArrays(currentPageEl);

        currentPageEl.classList.add('set--page-motion-ready');

        this._triggerMotionInterval(this.inViewElements);
    }

    /**
     * Gets the in-view and out-view elements and sets them in arrays easy access.
     * @param  {[type]} currentPageEl [description]
     * @return {[type]}               [description]
     */
    _getElementArrays(currentPageEl) {
        let motionEls = currentPageEl.querySelectorAll('[data-motion]');

        this._removeWatchers();

        this.inViewElements = [];
        this.outViewElements = [];

        [].forEach.call(motionEls, function(currentMotionEl, index) {
            //Remove any motion attributes from elements that are semantically hidden (think Slick cloned slides).
            //This prevents waiting for animations or displaying blank containers.
            if (currentMotionEl.closest('[aria-hidden="true"]')) {
                currentMotionEl.removeAttribute('data-motion');

                return;
            } else {
                let motionWatcher = scrollMonitor.create(currentMotionEl, this.elementOffset),
                    motionData = this.getMotionData(currentMotionEl, motionWatcher),
                    //This might not be the best way to check for visibility, but it works for now.
                    //Need to count only which elements are currently visible, the other elements can be shown on scroll.
                    elementIsVisible = currentMotionEl.offsetParent || (currentMotionEl.firstElementChild && currentMotionEl.firstElementChild.offsetHeight > 0);

                this._setMotionData(currentMotionEl, motionData, true);
                this._setupMotionElChildren(currentMotionEl, motionData);

                if ((motionWatcher.isInViewport && elementIsVisible) || motionData.immediate) {
                    //Push currently visible elements into an array,
                    //which is later used to display motion in a set interval.
                    this.inViewElements.push(currentMotionEl);
                } else {
                    //For the out-of-view elements, push scrollMonitor watchers into an array.
                    //Scale motion should always trigger automatically and not with scroll.
                    if (!motionData.hasScaleMotion) {
                        this.outViewElements.push(motionWatcher);
                    }

                    motionWatcher.one('enterViewport', this._enterViewportDebouncer.bind(this, currentMotionEl, index));
                }
            }
        }.bind(this));
    }

    /**
     * Debounces scrollMonitor's "enterViewport" event.
     * Pushes all newly "in viewport" elements into an array,
     * which is then passed to _triggerMotionInterval()
     * so that motion is triggered in the specified interval.
     * If this is not in place, motion will trigger at the same time
     * for elements that just entered the viewort.
     */
    _enterViewportDebouncer(currentMotionEl, orderPreference) {
        this.enterViewportEls = this.enterViewportEls || [];

        //Force the DOM order on the array.
        //`orderPreference` comes from the position in the DOM.
        //scrollMonitor sometimes fires in the wrong order when multiple elements are at the same height.
        this.enterViewportEls[orderPreference] = currentMotionEl;

        window.clearTimeout(this.enterViewportTimeout);

        this.enterViewportTimeout = window.setTimeout(function() {
            //Clean array out of empty items.
            this.enterViewportEls = this.enterViewportEls.filter(n => n);
            this._triggerMotionInterval(this.enterViewportEls, true);

            //Empty array after it has been passed to _triggerMotionInterval() to avoid looping through the same elements again.
            this.enterViewportEls = [];
        }.bind(this), 25);
    }

    /**
     * Ensures watchers are destroyed in case the `this.outViewElements` array needs to be rebuilt.
     */
    _removeWatchers() {
        if (this.outViewElements) {
            this.outViewElements.forEach(watchedElement => watchedElement.destroy);
        }
    }

    /**
     * Facilitates applying similar motion to a list of matching children elements.
     * This is also better than manually setting data-motion properties, because doing so would pause
     * full page transitions if the list of a data-motion children is too long.
     * @param  {[type]} motionEl   [description]
     * @param  {[type]} motionData [description]
     */
    _setupMotionElChildren(motionEl, motionData) {
        if (motionData.children) {
            [].forEach.call(motionData.children, function(currentChild) {
                currentChild.setAttribute('data-motion', motionData.propertiesArray.join(' '));

                this._setMotionData(currentChild, motionData, false);
            }.bind(this));
        }
    }

    /**
     * Very silly JSON string test. It just checks if the first character is an opening bracket,
     * because really that's all we need.
     */
    getMotionData(motionEl, motionWatcher) {
        let motionData = {},
            motionString = motionEl.getAttribute('data-motion'),
            propertiesString;

        if (motionString.charAt(0) === '{') {
            let parsedData = JSON.parse(motionString);

            motionData = parsedData;

            propertiesString = parsedData.properties;

            if (parsedData.children) {
                motionData.children = motionEl.querySelectorAll(parsedData.children);
            }
        } else {
            propertiesString = motionString;
        }

        motionData.propertiesArray = propertiesString.split(/(?:,| )+/);
        motionData.hasScaleMotion = propertiesString.indexOf('scale-') !== -1;

        this._setDirectionProperty(motionWatcher, motionData.propertiesArray);

        return motionData;
    }

    /**
     * Updates the "from-<direction>" property of a watched element with the direction based on the element's position in the DOM.
     * Leverages ScrollMonitor's data to determine where the element is positioned.
     */
    _setDirectionProperty(motionWatcher, propertiesArray = [], direction = 'from-v-direction') {
        let directionIndex = propertiesArray.indexOf(direction);

        if (directionIndex !== -1 && direction === 'from-v-direction') {
            propertiesArray[directionIndex] = motionWatcher.isInViewport || motionWatcher.isBelowViewport ? 'from-bottom' : 'from-top';
        }
    }

    /**
     * Sets the motionData properties into a targetEl (either an element with data-motion or a defined child).
     * @param {[type]} targetEl    [description]
     * @param {[type]} motionData  [description]
     * @param {[type]} setChildren [description]
     */
    _setMotionData(targetEl, motionData, setChildren) {
        targetEl.motionSettings = targetEl.motionSettings || {
            motionCompleteHandler: this._motionCompleteHandler,
            propertiesArray: motionData.propertiesArray.slice(0), //Slice array to create independent array references to elements and their children.
            children: setChildren && motionData.children ? motionData.children : '',
            scrollTracking: motionData.scrollTracking
        };

        PageMotion.toggleMotionClasses(targetEl, true);
    }

    /**
     * Fires the initial motion by setting a "set--motion-trigger" class, which has CSS transitions set.
     * @param  {[type]} motionEl [description]
     * @return {[type]}          [description]
     */
    triggerMotion(motionEl) {
        motionEl.classList.add('set--motion-trigger');

        this._triggerChildrenMotion(motionEl);

        motionEl.addEventListener(whichTransition(), motionEl.motionSettings.motionCompleteHandler);
    }

    _triggerChildrenMotion(motionEl) {
        //Fire children motions, if any.
        if (motionEl.motionSettings.children) {
            this._triggerMotionInterval(motionEl.motionSettings.children);
        }
    }

    /**
     * Forces elements to display a motion in a set interval.
     * The alternative, `window.setTimeout`, causes issues because browsers attempt to
     * reduce the amount of timers running at the same time.
     * @param  {NodeList} elementList [description]
     */
    _triggerMotionInterval(elementList) {
        let self = this,
            intervalCounter = 0,
            pageLoadInterval;

        pageLoadInterval = window.setInterval(function() {
            if (elementList[intervalCounter]) {
                self.triggerMotion(elementList[intervalCounter]);

                intervalCounter++;
            } else {
                window.clearInterval(pageLoadInterval);
            }
        }, self.motionInterval);
    }

    /**
     * After either an initial data-motion (or its children) element transition is complete,
     * reset said element to its original state to prevent issues with user-actionable transitions.
     * @param  {[type]} evt [description]
     */
    _motionCompleteHandler(evt) {
        if (evt.target === this) {
            this.removeAttribute('data-motion');
            this.classList.add('set--motion-complete');
            this.classList.remove('set--motion-trigger');

            PageMotion.toggleMotionClasses(this);

            publishEvent('motion', 'complete', this);

            this.removeEventListener(whichTransition(), this.motionSettings.motionCompleteHandler);
        }
    }

    /**
     * Sets motion classes on the target element depending on the motion types defined on its `data-motion` attribute.
     * @param  {[type]} targetEl [description]
     * @param  {[type]} add      [description]
     */
    static toggleMotionClasses(targetEl, add) {
        targetEl.motionSettings.propertiesArray.forEach(function(currentMotion) {
            if (add) {
                targetEl.classList.add(`set--motion-${currentMotion}`);
            } else {
                targetEl.classList.remove(`set--motion-${currentMotion}`);
            }
        });
    }

}
